import React, { useMemo, useEffect, useRef, forwardRef } from 'react'
import t from '~t'
import _ from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as collectionsActions from '~data/actions/collections'
import { makeTreeFlat, makeCollectionsStatus, selectMode, getBlankId } from '~data/selectors/collections'

import { withPause } from '~ui'
import Tree from './tree'
import SelectMode from './selectMode'

const CollectionsItems = forwardRef(function CollectionsItems(props, ref) {
    const {
        activeId,
        options = {}, //hideIds[], showGroups:true, search:''
        events = {}, //onItemClick, onGroupClick
        customRows, //[] additional items in tree
        customRowRenderer, //(row) renderer for additional items
        ...etc
    } = props

    const dispatch = useDispatch()
    const actions = useMemo(() => bindActionCreators(collectionsActions, dispatch), [dispatch])

    const getTree = useMemo(() => makeTreeFlat(), [])
    const getCollectionsStatus = useMemo(() => makeCollectionsStatus(), [])

    const data = useSelector(state => getTree(state, props))
    const status = useSelector(state => getCollectionsStatus(state))
    const selectModeValue = useSelector(state => selectMode(state))
    const blankId = useSelector(state => getBlankId(state))

    const latest = useRef({ activeId, status })
    latest.current = { activeId, status }

    useEffect(() => {
        actions.changeDefaults({
            items: [
                { _id: 0, title: t.s('allBookmarks') },
                { _id: -1, title: t.s('defaultCollection--1') },
                { _id: -99, title: t.s('defaultCollection--99') }
            ],
            groupTitle: _.capitalize(t.s('collectionsCount'))
        })

        actions.load()

        const refresh = () => actions.load()
        const createNewCollection = e => {
            if (typeof latest.current.activeId != 'object')
                actions.addBlank(latest.current.activeId, e.detail.asChild)
        }

        window.addEventListener('focus', refresh)
        window.addEventListener('create-new-collection', createNewCollection)

        return () => {
            actions.unselectAll()
            window.removeEventListener('focus', refresh)
            window.removeEventListener('create-new-collection', createNewCollection)
        }
    }, [actions])

    const prev = useRef({ activeId, status })
    useEffect(() => {
        const { activeId: prevActiveId, status: prevStatus } = prev.current
        prev.current = { activeId, status }

        //expand tree to active id
        if (prevActiveId != activeId || (prevStatus != status && status == 'loaded'))
            if (typeof activeId != 'object')
                actions.expandTo(activeId)

        //reset selection when active change
        if (prevActiveId != activeId)
            actions.unselectAll()
    }, [activeId, status, actions])

    const childProps = { ...etc, options, events, customRows, customRowRenderer, status, blankId, data, actions }

    return (
        <div className='relative h-full' role='list' ref={ref}>
            <SelectMode
                selectMode={selectModeValue}
                {...childProps} />

            <Tree
                {...childProps}
                activeId={selectModeValue.enabled ? selectModeValue.ids : activeId}
                data={data} />
        </div>
    )
})

export default withPause(CollectionsItems)
